import { AccountRate, RateStatus } from 'services/enums';

import { BILL_GENERATION_PAGE_PATTERN } from './routing/constants';

// Patterns

export const EDRPOU_PATTERN = new RegExp(
    /^(\d{8,10}|(([а-яА-ЯєЄІіЇїҐґ]{2}|[a-zA-Z]{2,8})\d{6}))$/,
);
// eslint-disable-next-line max-len, no-useless-escape
export const EMAIL_PATTERN = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
);
export const EMAIL_MAX_LENGTH = 254;
export const IPN_PATTERN = new RegExp(
    /^(\d{9}|\d{10}|\d{12}|(([а-яА-ЯєЄІіЇїҐґ]{2}|[a-zA-Z]{2,8})\d{6}))$/,
);
export const EDRPOU_IPN_PATTERN = new RegExp(
    /^(\d{8,10}|\d{9}|\d{10}|\d{12}|(([а-яА-ЯєЄІіЇїҐґ]{2}|[a-zA-Z]{2,8})\d{6}))$/,
);
export const PASSPORT_PATTERN = new RegExp(
    /^(([а-яА-ЯєЄІіЇїҐґ]{2}|[a-zA-Z]{2,8})\d{6})$/,
);
export const ID_CARD_PATTERN = new RegExp(/^(\d{9})$/);
export const FOP_PATTERN = new RegExp(
    `${IPN_PATTERN.source}|${PASSPORT_PATTERN.source}|${ID_CARD_PATTERN.source}`,
);
export const PHONE_PATTERN = new RegExp(/\+38[0-9_-]{10}/);
export const PRICE_PATTERN = new RegExp(/^\d+(\.\d{1,2})?$/);
export const NUMBERS_PATTERN = new RegExp(/^\d+$/);
export const UUID_PATTERN = new RegExp(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/,
);
export const CONTAINS_NUMBER_PATTERN = new RegExp(/\d/);
export const CONTAINS_SPECIALS_PATTERN = new RegExp(
    /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/,
);
export const COMMENT_TEXT_MAX_LENGTH = 2000;

// Web storage
export const WS_KEY_COMPLETE_REGISTRATION = 'isCompleteRegistrationSend';
export const WS_KEY_ES_HAS_ACTUAL_UPLOAD = 'esHasActualUpload';
export const WS_KEY_NOT_SHOW_OLD_BROWSER_ALERT = 'doNotShowOldBrowserAlert';
export const WS_KEY_REGISTRATION_ATTEMPTS = 'registrationAttempts';
export const WS_KEY_REGISTRATION_SOURCE = 'registrationSource';
export const WS_KEY_REMEMBER_KEYS = 'keysStored';
export const WS_KEY_SORT_DOCUMENTS_BY = 'sortDocumentsBy';
export const WS_KEY_FILTER_DOCUMENTS_BY = 'filterDocumentsBy';
export const WS_KEY_UNSUCCESSFULLY_SIGNED_DOCUMENTS =
    'unsuccessfullySignedDocuments';
export const WS_KEY_DOCUMENT_RECIPIENTS = 'documentRecipients';
export const WS_KEY_TMP_RECIPIENT_DATA = 'tmpRecipientData';
export const WS_KEY_HIDE_PRO_ONBOARDING = 'hideProOnboarding';
export const WS_KEY_ADD_INFO_POPUP_SHOWN = 'addInfoPopupShow';
export const WS_KEY_GUIDE_SIDEBAR_SHOWN = 'guideSidebarShow';
export const WS_KEY_GUIDE_ICON_HIGHLIGHTED = 'guideIconHighlighted';
export const WS_GRID_COLUMN_STATE_LS_KEY = 'DOCUMENTS_LIST:GRID_COLUMN_STATE';
export const WS_GRID_COLUMN_STATE_INTERNAL_LS_KEY =
    'DOCUMENTS_LIST:GRID_COLUMN_STATE:INTERNAL';
export const WS_GRID_COLUMN_STATE_INBOX_LS_KEY =
    'DOCUMENTS_LIST:GRID_COLUMN_STATE:INBOX';
export const WS_GRID_COLUMN_STATE_OUTGOING_LS_KEY =
    'DOCUMENTS_LIST:GRID_COLUMN_STATE:OUTGOING';
export const WS_GRID_COLUMN_STATE_ARCHIVE_LS_KEY =
    'DOCUMENTS_LIST:GRID_COLUMN_STATE:ARCHIVE';
export const WS_KEY_UPLOAD_DOCS_IDS = 'uploadDocsIds';

// Devices width
export const MEDIA_WIDTH = {
    wide: 1200,
    normal: 1024,
    tablet: 768,
    mobile: 480,
} as const;

export const ACCEPT_EXTENSION_LIST = [
    '.dbf',
    '.pdf',
    '.txt',
    '.xml',
    '.zip',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.rtf',
    '.jpg',
    '.jpeg',
    '.png',
] as const;

export const ACCEPT_EXTENSION_FOR_VERSIONED = [
    '.txt',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.pdf',
] as const;

export const ACCEPT_EXTENSION_FOR_ONLINE_EDIT = ['.doc', '.docx'];

// File mime types
export const ACCEPT_EXTENSIONS = ACCEPT_EXTENSION_LIST.join(',');
export const ACCEPT_EXTENSIONS_TEXT =
    'PDF, XML, DBF, TXT, PNG, JPEG, ZIP, DOCX, XLSX';

export const MIME_TYPE_MAP: Record<
    typeof ACCEPT_EXTENSION_LIST[number],
    string[]
> = {
    '.doc': ['application/msword'],
    '.docx': [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    '.xls': ['application/vnd.ms-excel'],
    '.xlsx': [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    '.pdf': ['application/pdf'],
    '.jpg': ['image/jpeg'],
    '.jpeg': ['image/jpeg'],
    '.png': ['image/png'],
    '.dbf': ['application/dbase'],
    '.xml': ['application/xml', 'text/xml', 'application/atom+xml'],
    '.txt': ['text/plain'],
    '.rtf': ['application/rtf'],
    '.zip': [
        'application/zip',
        'application/x-winzip',
        'application/x-zip',
        'multipart/x-zip',
        'application/x-zip-compressed',
    ],
} as const;
export const ACCEPT_VERSIONS_EXTENSIONS_TEXT = ACCEPT_EXTENSION_FOR_VERSIONED.map(
    (item) => item.toUpperCase().replace('.', ''),
).join(', ');
export const ACCEPT_GOOGLE_DRIVE_MIMETYPES = `
    application/vnd.google-apps.document,
    application/vnd.google-apps.spreadsheet
`;
export const ACCEPT_GOOGLE_DRIVE_MIMETYPES_TEXT = 'Google Docs, Google Sheets';
export const VIEWER_EXTENSIONS =
    '.dbf,.pdf,.txt,.xml,.jpg,.jpeg,.png,.doc,.docx,.xlsx';
export const ACCEPT_SIGNATURE_EXTENSIONS = '.dat, .zs2, .sk, .jks, .pk8, .pfx';
export const ACCEPT_VERSIONS_EXTENSIONS =
    '.txt, .doc, .docx, .xls, .xlsx, .pdf';

export const MAX_DOCUMENT_VERSIONS_COUNT = 50;

// CA and sign
export const CA_SERVERS = {
    dfs: 'acskidd.gov.ua',
    privat: 'acsk.privatbank.ua',
    test: 'ca.iit.com.ua',
    ukraina: 'uakey.com.ua',
    altersign: 'ca.altersign.com.ua',
};
export const CA_SERVERS_NAME = {
    dfs: 'Акредитований центр сертифікації ключів ІДД ДФС',
    minjust: 'АЦСК органів юстиції України',
    privat: [
        'АЦСК ПАТ КБ «ПРИВАТБАНК»',
        'АЦСК АТ КБ «ПРИВАТБАНК»',
        'АЦСК «ПРИВАТБАНК»',
    ],
};
export const CA_UKRAINE_ENCRYPTION_KEY_FILENAME_PATTERN = /\d*_(c|dc)\d*/i;
export const CERTIFICATE_DAYS_UNTIL_EXPIRED = 14;

// Others
export const BROWSERS_MIN_VERSION = {
    chrome: '>=84',
    firefox: '>=63',
    safari: '>=14.1',
    opera: '>=70',
    ie: '>=12',
    edge: '>=84',
};
export const MONITOR_VERSION_INTERVAL = 600000; // ms (10 minutes)
export const PHONE_PREFIX = '+380';
export const QUERY_DATE_FORMAT = 'YYYY-MM-DD';

export const PRO_FUNCTIONS = [
    'templates',
    'extraFields',
    'internalDocuments',
    'reviewers',
    'tags',
    'longTermStorage',
    'importantFields',
];

export const PRO_FUNCTIONS_LINKS = [
    'https://www.youtube.com/watch?v=grXtqsdUO2U',
    'https://www.youtube.com/watch?v=jNgGAiwOEbQ',
    'https://www.youtube.com/watch?v=fOVKJYuEklY',
    'https://www.youtube.com/watch?v=fOVKJYuEklY',
    'https://www.youtube.com/watch?v=WGmz9PxfQSQ',
] as const;

export const DEMO_REQUEST_URL =
    'https://crm.vchasno.com.ua/pub/form/37_zapit_na_demonstratsiyu_servisu/2fhgcq/';
export const PRO_RATE_INFO_URL = 'https://landing.vchasno.com.ua/pro-rate';

export const INTEGRATION_PRICE = 4.8;
export const INTEGRATION_PRICE_DISPLAY = '4,80';

// Ціна в гривнях за рік: 780 -> 780 грн/рік
export const INTEGRATION_FOP_PRICE_YEARLY = 780;
export const INTEGRATION_PRICE_YEARLY = 4800;
export const BASE_PRICE = 0;
export const TRIAL_PRICE = 0;
export const START_PRICE = 2400;
export const START_FOP_PRICE = 1800;
export const PRO_OLD = 7500;
export const PRO_PRICE = 7200;
export const PRO_FOP_PRICE = 4800;
export const PRO_TOV_PRICE = 9600;
export const PRO_PLUS_PRICE = 19200;
export const ARCHIVE_SMALL_PRICE = 480;
export const ARCHIVE_BIG_PRICE = 1440;

export const ACTIVE_COMPANIES_THOUSAND_COUNT = 600;

export const INTEGRATION_URL = `${BILL_GENERATION_PAGE_PATTERN}?rate=${AccountRate.INTEGRATION}`;
export const START_URL = `${BILL_GENERATION_PAGE_PATTERN}?rate=${AccountRate.START_2024_10}`;
export const PRO_URL = `${BILL_GENERATION_PAGE_PATTERN}?rate=${AccountRate.PRO_2024_10}`;
export const PRO_PLUS_URL = `${BILL_GENERATION_PAGE_PATTERN}?rate=${AccountRate.PRO_PLUS_2022_12}`;

export const INTEGRATION_RATE_CONSULTATION_URL =
    'https://vchasno.ua/integration/';

export const VCHASNO_EDRPOU = '41231992';

export const SESSION_EXPIRED_REDIRECT_LINK =
    '/auth/check-email?reason_logout=session_expired';

// Cookies
export const COOKIE_KEY_2FA_REMINDED = '2faReminded';
export const COOKIE_KEY_CRYPTO = '_gck'; // similar to GA cookies

// static links
export const OFFERS_LINK = 'https://service.vchasno.ua/oferty';
export const MANUAL_API_LINK =
    'https://docs.google.com/document/d/1yjDAensRbsY9-nujkKBPERyDG_LIAnrVzwE4Gfq0fzY/edit?usp=sharing';
export const INTEGRATION_1C_LINK = 'https://vchasno.ua/integration';
export const PRIVACY_POLICY_LINK = 'https://help.vchasno.com.ua/privacypolicy';
export const OFFERS_LINK_EDO = 'https://help.vchasno.com.ua/oferty';
export const PUBLIC_OFFER_LINK_EDO =
    'https://help.vchasno.com.ua/oferta-vid-01-01-2023';
export const PUBLIC_OFFER_LINK_EDI = 'https://help.edi.vchasno.com.ua/oferta';
export const PUBLIC_OFFER_LINK_KEP =
    'https://cap.vchasno.com.ua/publichnyj-dogovir-oferta';
export const PUBLIC_OFFER_LINK_KASSA = 'https://kasa.vchasno.com.ua/oferta';
export const GENERAL_PROVISIONS_LINK =
    'https://help.vchasno.com.ua/zagalni-polozhennya/';

// EDI
export const ELKO_EDRPOU = '39160089';
export const ROZETKA_EDRPOUS = ['37193071', '37568896', '30217001', '33584049'];
export const EDI_DELNOT_DOC_TYPE = 'DELNOT';

export const INACTIVE_RATE_STATUSES_SET = new Set([
    RateStatus.NEW,
    RateStatus.CANCELED,
    RateStatus.EXPIRED,
]);

// MAX_VALUES

export const MAX_TAGS_LENGTH = 255;

export default {
    EDRPOU_PATTERN,
    EMAIL_PATTERN,
    IPN_PATTERN,
    PHONE_PATTERN,
    UUID_PATTERN,

    WS_KEY_COMPLETE_REGISTRATION,
    WS_KEY_NOT_SHOW_OLD_BROWSER_ALERT,
    WS_KEY_REMEMBER_KEYS,
    WS_KEY_UNSUCCESSFULLY_SIGNED_DOCUMENTS,
    WS_KEY_TMP_RECIPIENT_DATA,

    COOKIE_KEY_2FA_REMINDED,

    MEDIA_WIDTH,

    ACCEPT_EXTENSIONS,
    ACCEPT_EXTENSIONS_TEXT,
    ACCEPT_VERSIONS_EXTENSIONS,
    ACCEPT_VERSIONS_EXTENSIONS_TEXT,
    ACCEPT_GOOGLE_DRIVE_MIMETYPES_TEXT,
    ACCEPT_GOOGLE_DRIVE_MIMETYPES,
    ACCEPT_SIGNATURE_EXTENSIONS,
    VIEWER_EXTENSIONS,

    MAX_DOCUMENT_VERSIONS_COUNT,

    CA_SERVERS,
    CA_SERVERS_NAME,
    CA_UKRAINE_ENCRYPTION_KEY_FILENAME_PATTERN,
    CERTIFICATE_DAYS_UNTIL_EXPIRED,

    BROWSERS_MIN_VERSION,
    MONITOR_VERSION_INTERVAL,
    PHONE_PREFIX,
    QUERY_DATE_FORMAT,

    PRO_FUNCTIONS,
    PRO_RATE_INFO_URL,

    MANUAL_API_LINK,
    INTEGRATION_1C_LINK,
    PRIVACY_POLICY_LINK,
    PUBLIC_OFFER_LINK: PUBLIC_OFFER_LINK_EDO,

    ROZETKA_EDRPOUS,
    EDI_DELNOT_DOC_TYPE,
};
